import { formatterId } from '../../../helpers/formatters';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const fechaLarga = (value) => {
    if (!value) return '';
    const [anio, mes, dia] = String(value).replace(/\//g, '-').split('-');
    if (!anio || !mes || !dia) return '';
    return `${Number(dia)} de ${MESES[Number(mes) - 1] || mes} de ${anio}`;
};

// El impreso ya trae el signo de pesos antes de la línea, así que el importe va
// sin él. Los centavos solo se escriben si existen: en una autorización de
// descuento no se puede redondear en silencio la cuota que van a retener.
const importe = (value) => {
    if (value !== 0 && !value) return '';
    const numero = Number(value) || 0;
    const entero = Math.trunc(numero);
    const centavos = Math.round(Math.abs(numero - entero) * 100);
    const miles = formatterId(entero);
    return centavos ? `${miles},${String(centavos).padStart(2, '0')}` : miles;
};

// La identificación se separa por miles, como en el contrato. Un NIT con dígito
// de verificación no es un número: en ese caso se escribe tal cual.
const identificacion = (valor) => {
    const texto = valor || valor === 0 ? String(valor).trim() : '';
    if (!texto) return '';
    const numero = Number(texto);
    return Number.isFinite(numero) ? formatterId(numero) : texto;
};

// Los campos "info*" del tercero llegan como "<id>|<nombre>" cuando el cliente se
// consulta por id; la búsqueda por patrón devuelve el nombre solo. La libranza se
// arma desde las dos, así que se acepta cualquiera de las dos formas.
const nombreInfo = (value) => {
    const texto = value ? String(value) : '';
    return texto.includes('|') ? texto.split('|')[1] : texto;
};

const nombreTercero = (info) => {
    if (!info) return '';
    if (info.razonSocial) return info.nombreComercial || info.razonSocial;
    return [info.nombre1, info.nombre2, info.apellido1, info.apellido2]
        .filter(Boolean)
        .join(' ')
        .trim();
};

// El formato no tiene de dónde sacar el concepto que devenga el deudor (salario,
// honorarios, pensión o el cargo): el tercero no guarda ocupación. Queda la línea
// del impreso para diligenciarla a mano.
const CONCEPTO_DEVENGADO = '';

/**
 * Arma la libranza con lo que ya preparó el formato F – CR - 03 de la solicitud:
 * son los mismos datos del cliente y del crédito, así que no se vuelve a
 * consultar el tercero.
 *
 * @param formato salida de buildFormatoData
 */
export const libranzaDesdeFormato = (formato) => {
    if (!formato) return null;

    const { cliente = {}, credito = {}, fecha = {}, ubicacionCliente = {} } = formato;

    return {
        numero: formato.numeroCredito || '',
        fecha: fecha.dia ? `${Number(fecha.dia)} de ${fecha.mes} de ${fecha.anio}` : '',
        montoTransado: credito.montoTransado || '',
        valorCuota: credito.valorCuota || '',
        cuotas: credito.cuotas ? String(credito.cuotas) : '',
        entidadPagadora: cliente.entidadPagadora || '',
        concepto: CONCEPTO_DEVENGADO,
        deudor: {
            nombre: (cliente.nombre || '').toUpperCase(),
            identificacion: identificacion(cliente.identificacion),
            // El buscador de clientes no trae estos dos; el formato los agrega
            // desde el registro completo cuando se pudo leer.
            lugarExpedicion: nombreInfo(cliente.lugarExpedicion) || ubicacionCliente.lugarExpedicion || '',
            ciudad: nombreInfo(cliente.ciudad) || ubicacionCliente.ciudad || '',
            direccion: cliente.direccion || '',
            telefono: cliente.celular || '',
            email: cliente.email || ''
        }
    };
};

/**
 * Arma la libranza para reimprimirla desde el crédito. El préstamo no trae el
 * lugar de expedición, la ciudad de residencia ni la dirección: salen del
 * registro del cliente, igual que en el contrato de novación.
 *
 * @param prestamo detalle del crédito (PrestamoDet)
 * @param cliente  tercero del cliente; opcional, los campos que aporta quedan
 *                 vacíos si no se pudo cargar
 */
export const libranzaDesdePrestamo = ({ prestamo, cliente }) => {
    if (!prestamo) return null;

    const info = cliente ? cliente.terceroInformacion : null;
    const ubicacion = cliente ? cliente.terceroUbicacion : null;

    return {
        numero: prestamo.idCredito ? String(prestamo.idCredito) : '',
        fecha: fechaLarga(prestamo.fechaSolicitud || prestamo.fecha),
        montoTransado: importe(prestamo.monto),
        valorCuota: importe(prestamo.valorCuota),
        cuotas: prestamo.plazo ? String(prestamo.plazo) : '',
        entidadPagadora: prestamo.nomPagaduria || '',
        concepto: CONCEPTO_DEVENGADO,
        deudor: {
            nombre: (nombreTercero(info) || prestamo.nomCliente || '').toUpperCase(),
            identificacion: identificacion(prestamo.idCliente),
            lugarExpedicion: nombreInfo(info?.infoMunicipioExp),
            ciudad: nombreInfo(ubicacion?.infoMunicipioRes),
            direccion: ubicacion?.direccionRes || '',
            telefono: ubicacion?.telefono1 || prestamo.telefono || '',
            email: ubicacion?.correo || prestamo.correo || ''
        }
    };
};
